import { T } from '@territory/tokens';
import { consoleRole } from './consoleRoles.js';

/* Scoped selector for a component root: 'button' -> '[data-rv-button]'. */
export const rvAttr = (name: string): string => `[data-rv-${name}]`;

/*
 * The one focus ring (CONVENTIONS.md): 2px accent outline, 2px offset, keyboard focus only. Pass the component's
 * data-rv name, or a full selector when the focusable element is a descendant of the root. The ring colour is the
 * base accent; a caller whose product draws another ring passes it as `color` (a console role read with the accent as
 * its fallback, so a product that does not set the role keeps the accent).
 */
export function focusRing(nameOrSelector: string, color: string = T.accent): string {
  const sel = nameOrSelector.startsWith('[') ? nameOrSelector : rvAttr(nameOrSelector);
  return `${sel}:focus-visible { outline: 2px solid ${color}; outline-offset: 2px; }\n${sel}:focus:not(:focus-visible) { outline: none; }`;
}

/*
 * The ring of a console button (spec 2d parity, lane 2114). The legacy admin page draws every button with
 * button:focus-visible{outline:2px solid var(--accentGlow);outline-offset:2px}, the accent glow in dark and, through
 * :root[data-theme=light] :focus-visible, the accent in light. The role button-focus-ring carries both reads in
 * products.admin; a product that leaves it unset gets the base accent, the same ring focusRing draws without a color.
 * Pass it as the `color` of a control that stands in for a legacy <button>. A control that stands in for a legacy element
 * with its own, more specific focus rule (an input, a link, .adv-tab, .adv-seg) keeps the base accent.
 */
export const consoleButtonRing: string = consoleRole('button-focus-ring', T.accent);
